import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useSEO } from "../components/SEO";
import { JsonLd } from "../components/JsonLd";
import { serviceSchema } from "../lib/schema";
import { PageHero, Reveal, SectionTag } from "../components/Reveal";
import { BookCallButton } from "../components/CTA";
import { CtaBand } from "../components/CtaBand";
import { IMAGES } from "../config/images";

const TOPICS = [
  { title: "Always-On Campaigns", text: "Umrah runs year-round. Your lead generation should too: steady, measurable, never seasonal-only." },
  { title: "Seasonal Promotions", text: "Ramadan, school holidays and winter departures each deserve their own campaign moment." },
  { title: "Audience Segmentation", text: "Families, first-time pilgrims, groups and repeat travellers respond to different messages." },
  { title: "Regional Targeting", text: "City and state-level targeting: Mumbai, Surat, Ahmedabad, Hyderabad, Delhi, Lucknow, Kerala and beyond." },
  { title: "WhatsApp Enquiries", text: "Campaigns that land directly in WhatsApp, where Indian customers prefer to talk." },
  { title: "Retargeting", text: "Re-engage visitors who compared packages but didn't enquire the first time." },
  { title: "Email Follow-Up", text: "Nurture sequences that keep your agency present while families decide." },
  { title: "Customer Nurturing", text: "Past enquiries and past pilgrims are a growth channel, treated accordingly." },
];

export default function UmrahMarketing() {
  useSEO(
    "Umrah Marketing Agency in India | Nexus Catalyst",
    "Umrah marketing for Indian travel agencies: always-on campaigns, regional targeting, WhatsApp enquiries, retargeting and email follow-up."
  );
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [-50, 50]);

  return (
    <main data-testid="umrah-marketing-page">
      <JsonLd
        data={serviceSchema({
          name: "Umrah Marketing for Indian Travel Agencies",
          description: "Always-on Umrah lead generation campaigns, regional targeting, WhatsApp enquiries and email follow-up for Indian travel agencies.",
          path: "/umrah-marketing",
        })}
      />
      <PageHero
        tag="Umrah Marketing"
        testid="umrah-hero"
        crumbs={[{ label: "Umrah Marketing", to: "/umrah-marketing" }]}
        lines={[
          "Build a stronger",
          <>Umrah <span className="text-tan">lead pipeline.</span></>,
        ]}
        copy="Umrah demand never fully switches off, which means your marketing shouldn't either. We build always-on acquisition systems with room for seasonal pushes."
      >
        <BookCallButton label="Build My Umrah Growth Plan" testid="umrah-hero-cta" />
      </PageHero>

      <section className="bg-cream py-24 md:py-32" data-testid="umrah-topics">
        <div className="max-w-7xl mx-auto px-6">
          <Reveal className="mb-14 max-w-3xl">
            <SectionTag className="mb-6">The Umrah System</SectionTag>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-charcoal leading-[1.05]">
              Always-On, Regionally Targeted, WhatsApp-First.
            </h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TOPICS.map((t, i) => (
              <Reveal key={t.title} delay={i * 0.05} className="h-full">
                <div
                  className="h-full bg-white border border-charcoal/10 p-7 transition-colors duration-300 hover:border-gold/50"
                  data-testid={`umrah-topic-${i + 1}`}
                >
                  <span className="text-xs font-semibold tracking-[0.25em] text-gold">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-4 text-lg font-semibold tracking-tight text-charcoal">{t.title}</h3>
                  <p className="mt-2 text-sm text-charcoal/60 leading-relaxed">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section ref={imgRef} className="relative h-[380px] md:h-[480px] overflow-hidden" data-testid="umrah-image-band">
        <motion.img
          src={IMAGES.architectureCourtyard}
          alt="Serene white mosque courtyard"
          style={{ y }}
          className="absolute inset-0 w-full h-full object-cover scale-110"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-charcoal/55" />
        <div className="relative max-w-7xl mx-auto px-6 h-full flex items-center">
          <Reveal>
            <p className="text-2xl md:text-4xl font-bold tracking-tight text-cream max-w-2xl leading-snug">
              A predictable pipeline turns "how was this month?" into "how do
              we handle the volume?"
            </p>
          </Reveal>
        </div>
      </section>

      <CtaBand
        title="Ready to Build Your Umrah Pipeline?"
        copy="Share your packages and target cities. We'll outline an always-on growth plan built around them."
        testid="umrah-cta-band"
      />
    </main>
  );
}
